import { useEffect, useRef, useState } from 'react';

import { ReactionType } from '@genially/p2p-lib';

import { usePlayerClientContext } from '../../../../../PlayerClientProvider/hooks/usePlayerClientContext';
import { ReactionButton } from './components/ReactionButton/ReactionButton';
import { ArrowButton, ReactionsContainer, ReactionsScrollArea } from './Reactions.styled';

const REACTIONS: { type: ReactionType; emoji: string }[] = [
  { type: ReactionType.Heart, emoji: '❤️' },
  { type: ReactionType.ThumbUp, emoji: '👍' },
  { type: ReactionType.Confetti, emoji: '🎉' },
  { type: ReactionType.DanceWoman, emoji: '💃' },
  { type: ReactionType.Claps, emoji: '👏' },
  { type: ReactionType.Lol, emoji: '😂' },
  { type: ReactionType.Surprise, emoji: '😮' },
  { type: ReactionType.Cry, emoji: '😢' },
  { type: ReactionType.Think, emoji: '🤔' },
  { type: ReactionType.ThumbDown, emoji: '👎' },
];

const BUTTON_WIDTH = 40; // px — matches StyledReactionButton width (size.units(5))

export const Reactions = () => {
  const { client } = usePlayerClientContext();

  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    const el = scrollAreaRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: checkScroll does not need to be in the dependency array
  useEffect(() => {
    const el = scrollAreaRef.current;
    if (!el) return;

    checkScroll(); // initial check on mount

    el.addEventListener('scroll', checkScroll);

    const resizeObserver = new ResizeObserver(checkScroll);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener('scroll', checkScroll);
      resizeObserver.disconnect();
    };
  }, []);

  const scrollLeft = () =>
    scrollAreaRef.current?.scrollBy({ left: -BUTTON_WIDTH, behavior: 'smooth' });
  const scrollRight = () =>
    scrollAreaRef.current?.scrollBy({ left: BUTTON_WIDTH, behavior: 'smooth' });

  const handleClick = (reactionType: ReactionType) => {
    client.sendReaction(reactionType);
  };

  return (
    <ReactionsContainer>
      {canScrollLeft && (
        <ArrowButton aria-label="scroll reactions left" onClick={scrollLeft}>
          ‹
        </ArrowButton>
      )}
      <ReactionsScrollArea ref={scrollAreaRef} data-cy="reactions-scroll-area">
        {REACTIONS.map(({ type, emoji }) => (
          <ReactionButton key={type} type={type} emoji={emoji} onClick={handleClick} />
        ))}
      </ReactionsScrollArea>
      {canScrollRight && (
        <ArrowButton aria-label="scroll reactions right" onClick={scrollRight}>
          ›
        </ArrowButton>
      )}
    </ReactionsContainer>
  );
};
